import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import API from '../api';
import Modal from './Modal';
import AdminBrandCategories from './AdminBrandCategories';
import { slugify, sanitizeSlugInput } from '../utils/slug';

const EMPTY = { name: '', slug: '', category_id: '', logo_url: '', description: '', sort_order: 0, is_active: 1 };
const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';

export default function AdminBrands() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [catalogBrand, setCatalogBrand] = useState(null); // brand whose categories are being managed

  const load = () =>
    Promise.all([API.get('/brands/all'), API.get('/categories/all')])
      .then(([b, c]) => { setItems(b.data); setCategories(c.data); })
      .catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    const nextOrder = items.length ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;
    setForm({ ...EMPTY, sort_order: nextOrder });
    setModal({ mode: 'add' });
  };
  const openEdit = (item) => { setForm({ ...item, category_id: item.category_id || '' }); setModal({ mode: 'edit', id: item.id }); };

  const handleNameChange = (name) => {
    setForm(f => ({ ...f, name, slug: modal?.mode === 'add' ? slugify(name) : f.slug }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, logo_url: data.url }));
    } catch { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, category_id: form.category_id || null };
      if (modal.mode === 'add') await API.post('/brands', payload);
      else await API.put(`/brands/${modal.id}`, payload);
      setModal(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this brand? Its categories and sub categories will be deleted too, and products assigned to it will lose their brand.')) return;
    await API.delete(`/brands/${id}`); load();
  };

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-dark">Brands</h1>
          <p className="text-sm text-gray-500 mt-1">Manage the brands Sankyo carries, organized by product line</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm flex items-center gap-2">
          <PlusIcon className="w-4 h-4" /> Add Brand
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-gray-300 p-12 text-center text-gray-400">
            No brands yet. Click "Add Brand" to get started.
          </div>
        )}
        {items.map(item => (
          <div key={item.id} className="bg-white border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
            <div className="h-16 flex items-center justify-center bg-gray-50 border border-gray-100">
              {item.logo_url
                ? <img src={imgSrc(item.logo_url)} alt={item.name} className="max-h-12 max-w-full object-contain" />
                : <span className="text-gray-400 text-xs">No Logo</span>}
            </div>
            <div>
              <div className="font-semibold text-gray-800 text-sm">{item.name}</div>
              <div className="text-xs text-gray-400 mt-0.5">{item.category_name || 'No product line assigned'}</div>
              {item.description && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</div>}
            </div>
            <button onClick={() => setCatalogBrand(item)}
              className="mt-auto flex items-center justify-center gap-1.5 text-xs font-medium py-2 border border-primary/40 text-primary hover:bg-primary hover:text-white transition-colors">
              <Squares2X2Icon className="w-3.5 h-3.5" />
              Categories ({item.catalog_category_count || 0})
            </button>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <span className={`text-xs font-medium px-2 py-0.5 ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {item.is_active ? 'Active' : 'Hidden'}
              </span>
              <div className="flex gap-2">
                <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-primary border border-gray-200 hover:border-primary transition-colors">
                  <PencilIcon className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {catalogBrand && (
        <AdminBrandCategories brand={catalogBrand} onClose={() => { setCatalogBrand(null); load(); }} />
      )}

      {modal && (
        <Modal title={modal.mode === 'add' ? 'Add Brand' : 'Edit Brand'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Name *</label>
              <input value={form.name} onChange={e => handleNameChange(e.target.value)}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Slug *</label>
              <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: sanitizeSlugInput(e.target.value) }))}
                className="w-full border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Product Line</label>
              <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                <option value="">— No Product Line —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">Which section this brand appears under on the Brands page.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
              <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={3} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary resize-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Logo</label>
              {form.logo_url && (
                <div className="h-16 bg-gray-50 border border-gray-200 flex items-center justify-center mb-2">
                  <img src={imgSrc(form.logo_url)} alt="logo preview" className="max-h-12 max-w-full object-contain" />
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer mb-2" />
              <input value={form.logo_url || ''} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))}
                placeholder="or paste logo URL..." className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value={1}>Active</option>
                  <option value={0}>Hidden</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || !form.name || !form.slug} className="btn-primary text-sm disabled:opacity-60 flex-1">
                {saving ? 'Saving...' : 'Save Brand'}
              </button>
              <button onClick={() => setModal(null)} className="btn-outline text-sm flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
